import type {ReactElement, ReactNode} from 'react';
import React, {useEffect, useState} from 'react';
import {useSubmitAuthFormOnce} from '@Hooks/login-signup/useSubmitAuthFormOnce';
import {validateUserPassword} from '@Libraries/user.library';
import {
  AUTH_MODE,
  AUTH_TYPE,
  EMAIL_PLACEHOLDER,
  getCommonAuthFormParams,
  getConfirmPasswordStatus,
  getPasswordFieldStatus,
  getRecaptchaSelectorValue,
  getPolicyItemStatusAfterRejectedSubmit,
  getStatusForPasswordLength,
  getStatusForSpecialCharactersInPassword,
  PASSWORD_PLACEHOLDER,
  redirectToPage,
} from '@Libraries/login-signup-library';
import {executeThunk} from '@Utils/thunk.util';
import {submitAuthForm} from '@Components/login-page/login-page-thunk';
import type {AsyncSignupParams, AuthFormSubmissionParams, BaseAuthFormProps, WebSignupParams} from '@Components/login-page/login-page.types';
import {AuthFormSubmissionType} from '@Components/login-page/login-page.types';
import {PasswordPolicyItem} from '@Components/signup-form/components/password-policy-item';
import {AnimatePresence, motion} from 'framer-motion';
import {useIsSignupBlocked, useLoginPageAuthButtonsDisabled, useLoginPageAuthMode, useLoginPageAuthType, useLoginPageSignupEmail} from '@Components/login-page/login-page.hooks';
import {blockSignup, updateSignupEmail} from '@Components/login-page/login-page-reducer';
import {isValidEmail} from '@Utils/string.util';
import {useAppDispatch, useAppSelector} from '@/hooks';
import {AuthFormSubmitButton} from '../auth-form-submit-button';
import {EmailUpdateNotifications} from './components/email-update-notifications';
import {ButtonType} from '@Components/button';
import {INPUT_FIELD_TYPE} from '../input-field';
import {AuthInput} from '../auth-input';
import styles from './signup-form.module.scss';
import {useUpdateErrorStates} from '@/hooks/login-signup/useUpdateErrorStates';
import {PASSWORD_POLICY_ITEM_STATUS, SignupFailureReason} from './signup-form.types';
import {useOpenTwoFactorVerification} from '@Hooks/login-signup/useOpenTwoFactorVerification';
import {closeTwoFactorAuthenticationVerificationModalBefore} from '@Modals/two-factor-authentication-verification-modal/two-factor-authentication-verification-modal.library';
import {AuthTestId} from '@/test-ids/auth-test-id';

interface SignupFormProps extends BaseAuthFormProps {
  /**
   * Supplied only by surfaces that can host the second signup panel — currently the login page. Without it
   * the form stays single-step, which is what the in-editor modal still renders.
   */
  onEmailStepComplete?: () => void;
}

const PASSWORD_POLICY_ANIMATION_DURATION = 0.2;

const SIGNUP_RECAPTCHA_SELECTOR = 'js-g-recaptcha-response-signup';

export function SignupForm({showSmallButton = false, onAuthFormSubmitSuccess = $.noop, onEmailStepComplete}: SignupFormProps): ReactElement<any> {
  const disableSignupButton = useLoginPageAuthButtonsDisabled();
  const authMode = useLoginPageAuthMode();
  const authType = useLoginPageAuthType();
  const carriedSignupEmail = useLoginPageSignupEmail();
  const isSignupBlocked = useIsSignupBlocked();
  const dispatch = useAppDispatch();
  const {submitAuthFormOnce} = useSubmitAuthFormOnce();
  const {showErrors} = useUpdateErrorStates();
  const {openTwoFactorVerification} = useOpenTwoFactorVerification();
  const signupSource = useAppSelector((state) => {
    return state.loginPage.signupSource;
  });
  const signupActionType = useAppSelector((state) => {
    return state.loginPage.signupActionType;
  });
  const isInDataRegulatedLocation = useAppSelector((state) => {
    return state.loginPage.isInDataRegulatedLocation;
  });

  const [email, setEmail] = useState(carriedSignupEmail);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [emailUpdates, setEmailUpdates] = useState(!isInDataRegulatedLocation);
  const [showPasswordFieldError, setShowPasswordFieldError] = useState(false);
  const [isEmailInvalid, setIsEmailInvalid] = useState(false);
  const [doPasswordsMatch, setDoPasswordsMatch] = useState(false);
  const [showPasswordMismatchError, setShowPasswordMismatchError] = useState(false);
  const [passwordFieldIcon, setPasswordFieldIcon] = useState('icon-eye-off');
  const [confirmPasswordFieldIcon, setConfirmPasswordFieldIcon] = useState('icon-eye-off');
  const [showPasswordPolicyCriteria, setShowPasswordPolicyCriteria] = useState(false);
  const [passwordHasFocus, setPasswordHasFocus] = useState(false);
  const [confirmPasswordHasFocus, setConfirmPasswordHasFocus] = useState(false);
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);

  useEffect(() => {
    setShowPasswordPolicyCriteria(shouldPasswordPolicyBeShown);
  }, [passwordHasFocus, password, hasAttemptedSubmit]);

  const handleEmailInputChange = React.useCallback((txt: string) => {
    setEmail(txt.trim());
  }, []);

  const setPasswordMismatchErrorState = (state: boolean): void => {
    setShowPasswordMismatchError(state);
  };

  const handlePasswordInputChange = React.useCallback(
    (txt: string) => {
      setHasAttemptedSubmit(false);
      setDoPasswordsMatch(txt === confirmPassword);
      setPasswordMismatchErrorState(!(txt === confirmPassword));
      setPassword(txt.trim());
    },
    [showPasswordMismatchError, confirmPassword]
  );

  const showError = (): boolean => {
    return showPasswordFieldError || showPasswordMismatchError;
  };

  const getNewPasswordFieldIcon = (icon: string): string => {
    return icon === 'icon-eye' ? 'icon-eye-off' : 'icon-eye';
  };

  const onPasswordEyeIconClick = React.useCallback(() => {
    setPasswordFieldIcon(getNewPasswordFieldIcon);
  }, []);

  const onConfirmPasswordEyeIconClick = React.useCallback(() => {
    setConfirmPasswordFieldIcon(getNewPasswordFieldIcon);
  }, []);

  const handleConfirmPasswordInputChange = React.useCallback(
    (txt: string) => {
      if (showPasswordMismatchError) {
        setPasswordMismatchErrorState(false);
      }

      setHasAttemptedSubmit(false);
      setConfirmPassword(txt.trim());

      if (!validateUserPassword(password)) {
        return;
      }

      handlePasswordsComparison(txt);
    },
    [showPasswordMismatchError, password]
  );

  const handlePasswordsComparison = (txt: string): void => {
    if (txt !== password && txt.length > 0) {
      setShowPasswordFieldError(true);
      setDoPasswordsMatch(false);
    } else {
      setShowPasswordFieldError(false);
      if (txt.length > 0) {
        setDoPasswordsMatch(true);
      }
    }
  };

  const shouldPasswordPolicyBeShown = (): boolean => {
    return passwordHasFocus || password.length > 0 || hasAttemptedSubmit;
  };

  const getPolicyItemStatus = (statusWhileEditing: PASSWORD_POLICY_ITEM_STATUS): PASSWORD_POLICY_ITEM_STATUS => {
    return hasAttemptedSubmit ? getPolicyItemStatusAfterRejectedSubmit(statusWhileEditing) : statusWhileEditing;
  };

  /** Single-step surfaces show every field at once, as signup always has. */
  const isPasswordStep = (): boolean => {
    return onEmailStepComplete === undefined || authType === AUTH_TYPE.SIGNUP_PASSWORD;
  };

  const isEmailStep = (): boolean => {
    return onEmailStepComplete !== undefined && authType !== AUTH_TYPE.SIGNUP_PASSWORD;
  };

  /** The first step only collects the address; the password lives on its own panel. */
  const onContinue = (): void => {
    const isInvalidEmail = !isValidEmail(email);
    setIsEmailInvalid(isInvalidEmail);
    if (isInvalidEmail) {
      return;
    }

    dispatch(updateSignupEmail(email));
    onEmailStepComplete?.();
  };

  /**
   * Enter commits whichever step is on screen. Without the split it always ran the password-step submit,
   * which fails its own password check on the email step and leaves the key doing nothing.
   */
  const onFormSubmit = (): void => {
    if (isEmailStep()) {
      onContinue();
      return;
    }
    onSubmit();
  };

  const onSubmit = () => {
    const isInvalidEmail = !isValidEmail(email);
    setIsEmailInvalid(isInvalidEmail);
    if (isInvalidEmail) {
      return;
    }

    if (!validateUserPassword(password) || confirmPassword.length === 0) {
      setHasAttemptedSubmit(true);
      setShowPasswordMismatchError(true);
      return;
    }

    if (password !== confirmPassword) {
      return;
    }

    submitAuthFormOnce(onSignupSubmit, SIGNUP_RECAPTCHA_SELECTOR);
  };

  const onCheckboxClick = React.useCallback(() => {
    setEmailUpdates(!emailUpdates);
  }, [emailUpdates]);


  const getWebSignupParams = () => {
    const webSignupParams: WebSignupParams = {
      email,
      password,
      confirmPassword,
      emailUpdates,
      gRecaptchaResponse: getRecaptchaSelectorValue(),
      ...getCommonAuthFormParams(),
    };
    return webSignupParams;
  };

  const getAsyncSignupParams = () => {
    const asyncSignupParams: AsyncSignupParams = {
      email,
      password,
      confirmPassword,
      gRecaptchaResponse: getRecaptchaSelectorValue(),
      signupSource,
      emailUpdates,
    };
    return asyncSignupParams;
  };

  const onSignupSubmit = async () => {
    switch (authMode) {
      case AUTH_MODE.ASYNC:
        await onAsyncSignupSubmit();
        break;
      case AUTH_MODE.DEFAULT:
        await onWebSignupSubmit();
        break;
    }
  };

  const onWebSignupSubmit = async () => {
    const authFormSubmissionParams: AuthFormSubmissionParams = {
      type: AuthFormSubmissionType.WEBSIGNUP,
      requestParams: getWebSignupParams(),
    };

    await executeThunk(
      () => {
        return dispatch(submitAuthForm(authFormSubmissionParams));
      },
      onWebSignupSuccess,
      handleWebSignupError
    );
  };

  const onAsyncSignupSubmit = async () => {
    const authFormSubmissionParams: AuthFormSubmissionParams = {
      type: AuthFormSubmissionType.ASYNCSIGNUP,
      requestParams: getAsyncSignupParams(),
    };

    await executeThunk(
      () => {
        return dispatch(submitAuthForm(authFormSubmissionParams));
      },
      onAsyncSignupSuccess,
      handleAsyncSignupError
    );
  };

  const onWebSignupSuccess = (response: any): void => {
    if ('pendingMFA' in response && response.pendingMFA) {
      openTwoFactorVerification();
      return;
    }

    window.PMW.gtm.trackSignupEvents(window.PMW.gtm.SIGNUP_LABELS.EMAIL);
    if (window.PMW.util.doesUrlContainEmbeddedEditorAuthenticationLayoverUri(response.redirectURL)) {
      window.close();
      return;
    }

    redirectToPage(window.PMW.util.site_url(response.redirectURL));
  };

  const onAsyncSignupSuccess = (response: any): void => {
    if ('pendingMFA' in response && response.pendingMFA) {
      openTwoFactorVerification({onVerificationSuccessCallback: closeTwoFactorAuthenticationVerificationModalBefore(onAuthFormSubmitSuccess)});
      return;
    }

    window.PMW.gtm.trackSignupEvents(window.PMW.gtm.SIGNUP_LABELS.EMAIL);
    onAuthFormSubmitSuccess();
  };

  const handleWebSignupError = (response: any): void => {
    if (response.payload.data.reason === SignupFailureReason.TOO_MANY_ATTEMPTS_FROM_DEVICE) {
      dispatch(blockSignup());
    }

    if (response.payload.data.redirectUrl === null || response.payload.data.redirectUrl === undefined) {
      showErrors(response.payload.data.message);
    } else if (response.payload.data.redirectUrl === 'authenticate/handleSignupError') {
      redirectToPage(window.PMW.util.site_url(`${response.payload.data.redirectUrl}?message=${response.payload.data.message}`));
    } else {
      redirectToPage(window.PMW.util.site_url(response.payload.data.redirectUrl));
    }
  };

  const handleAsyncSignupError = (response: any): void => {
    let errorMessage = '';
    const errRes = response.payload;

    if (errRes.status === 'fail' && errRes.data) {
      if (errRes.data.reason === SignupFailureReason.TOO_MANY_ATTEMPTS_FROM_DEVICE) {
        dispatch(blockSignup());
      }
      errorMessage = getErrorMessageFromAsyncSignupResponse(errRes.data);
    } else {
      errorMessage = window.i18next.t('pmwjs_unknown_error');
    }

    if (errorMessage) {
      showErrors(errorMessage);
    }
  };

  /** A rejected submit with a valid password and an empty confirmation says why, rather than doing nothing. */
  const showPasswordMatchMessage = (): boolean => {
    if (hasAttemptedSubmit && validateUserPassword(password)) {
      return true;
    }
    return confirmPassword.length > 0 && password.length > 8;
  };

  const onPasswordFocus = (): void => {
    setPasswordHasFocus(true);
  };

  const onPasswordFocusOut = (): void => {
    setPasswordHasFocus(false);
  };

  const onConfirmPasswordFocus = (): void => {
    setConfirmPasswordHasFocus(true);
  };

  const onConfirmPasswordFocusOut = (): void => {
    setConfirmPasswordHasFocus(false);
  };

  const onEmailFocusOut = (): void => {
    if (email === '') {
      return;
    }
    setIsEmailInvalid(!isValidEmail(email));
  };

  const getPasswordPolicyCriteria = (): ReactNode => {
    return (
      <motion.div
        initial={{opacity: 0}}
        animate={{opacity: 1}}
        exit={{opacity: 0}}
        transition={{duration: PASSWORD_POLICY_ANIMATION_DURATION}}
        className={styles.passwordPolicyContainer}
      >
        <PasswordPolicyItem data-testid={AuthTestId.PASSWORD_POLICY_LENGTH} text={window.i18next.t('pmwjs_password_policy_length')} status={getPolicyItemStatus(getStatusForPasswordLength(password, passwordHasFocus))} />
        <PasswordPolicyItem data-testid={AuthTestId.PASSWORD_POLICY_CHARACTERS} text={window.i18next.t('pmwjs_password_policy_characters')} status={getPolicyItemStatus(getStatusForSpecialCharactersInPassword(password, passwordHasFocus))} />
      </motion.div>
    );
  };

  const getPasswordMatchResult = (): ReactNode => {
    if (!showPasswordMatchMessage()) {
      return null;
    }

    if (doPasswordsMatch) {
      return <PasswordPolicyItem text={window.i18next.t('pmwjs_passwords_match')} status={PASSWORD_POLICY_ITEM_STATUS.MATCH} />;
    }

    if (showError()) {
      return <PasswordPolicyItem data-testid={AuthTestId.PASSWORD_POLICY_MISMATCH} text={window.i18next.t('pmwjs_passwords_do_not_match')} status={PASSWORD_POLICY_ITEM_STATUS.MISMATCH} />;
    }
    return null;
  };

  const getFormInputFields = (): ReactNode => {
    return (
      <>
        <AuthInput
          data-testid={AuthTestId.EMAIL_INPUT}
          errorTestId={AuthTestId.EMAIL_ERROR}
          label={window.i18next.t('pmwjs_email')}
          placeholder={EMAIL_PLACEHOLDER}
          type={INPUT_FIELD_TYPE.EMAIL}
          value={email}
          handleInput={handleEmailInputChange}
          showError={isEmailInvalid}
          isInvalid={isSignupBlocked}
          onInputSubmit={onFormSubmit}
          errorText={window.i18next.t('pmwjs_valid_email_address')}
          onFocusOutCallback={onEmailFocusOut}
          enableFocusStyles
        />

        {getPasswordFields()}
      </>
    );
  };

  const getPasswordFields = (): ReactNode => {
    if (!isPasswordStep()) {
      return null;
    }

    return (
      <>
        <AnimatePresence initial={false}>
          <div className={styles.passwordFieldContainer}>
            <AuthInput
              data-testid={AuthTestId.PASSWORD_INPUT}
              label={window.i18next.t('pmwjs_password')}
              type={INPUT_FIELD_TYPE.PASSWORD}
              placeholder={PASSWORD_PLACEHOLDER}
              handleInput={handlePasswordInputChange}
              onPasswordIconEyeClick={onPasswordEyeIconClick}
              passwordFieldIcon={passwordFieldIcon}
              onInputSubmit={onFormSubmit}
              onFocusCallback={onPasswordFocus}
              onFocusOutCallback={onPasswordFocusOut}
              passwordStatus={getPasswordFieldStatus(password, passwordHasFocus)}
              isInvalid={isSignupBlocked}
              enableFocusStyles
            />
            {showPasswordPolicyCriteria ? getPasswordPolicyCriteria() : null}
          </div>
        </AnimatePresence>

        <div className={styles.confirmPasswordContainer}>
          <AuthInput
            data-testid={AuthTestId.CONFIRM_PASSWORD_INPUT}
            label={window.i18next.t('pmwjs_confirm_password')}
            type={INPUT_FIELD_TYPE.PASSWORD}
            placeholder={PASSWORD_PLACEHOLDER}
            handleInput={handleConfirmPasswordInputChange}
            onPasswordIconEyeClick={onConfirmPasswordEyeIconClick}
            passwordFieldIcon={confirmPasswordFieldIcon}
            onInputSubmit={onFormSubmit}
            onFocusCallback={onConfirmPasswordFocus}
            onFocusOutCallback={onConfirmPasswordFocusOut}
            passwordStatus={getConfirmPasswordStatus(password, confirmPassword, passwordHasFocus, confirmPasswordHasFocus)}
            isInvalid={isSignupBlocked}
            enableFocusStyles
          />

          <div className={styles.passwordMatchSlot}>{getPasswordMatchResult()}</div>
        </div>
      </>
    );
  };

  const getEmailUpdatesCheckbox = (): ReactNode => {
    return <EmailUpdateNotifications onCheckboxClick={onCheckboxClick} emailUpdates={emailUpdates} disabled={isSignupBlocked} />;
  };

  const getFormLayoutClasses = (): string => {
    if (isEmailStep()) {
      return styles.formLayout;
    }
    return `${styles.formLayout} ${styles.formLayoutWithConsent}`;
  };

  const getFormBody = (): ReactNode => {
    if (isEmailStep()) {
      return (
        <>
          {getFormInputFields()}
          {getSubmitButton()}
        </>
      );
    }

    return (
      <>
        <div className={styles.fieldSection}>{getFormInputFields()}</div>
        <div className={styles.consentSection}>
          {getEmailUpdatesCheckbox()}
          {getSubmitButton()}
        </div>
      </>
    );
  };

  const getSubmitButton = (): ReactNode => {
    if (isEmailStep()) {
      return (
        <AuthFormSubmitButton
          data-testid={AuthTestId.SUBMIT_BUTTON}
          btnText={window.i18next.t('pmwjs_continue')}
          customClasses="-fullwidth"
          onClick={onFormSubmit}
          showSmallButton={showSmallButton}
          type={ButtonType.PRIMARY}
        />
      );
    }

    return (
      <AuthFormSubmitButton
        data-testid={AuthTestId.SUBMIT_BUTTON}
        btnText={window.i18next.t('pmwjs_create_account')}
        customClasses="-fullwidth"
        isDisabled={disableSignupButton || isSignupBlocked}
        onClick={onFormSubmit}
        showSmallButton={showSmallButton}
        type={ButtonType.PRIMARY}
      />
    );
  };

  return (
    <div className={getFormLayoutClasses()}>
      <input type="hidden" name="gRecaptchaResponse" className={`g-recaptcha-response ${SIGNUP_RECAPTCHA_SELECTOR}`} data-recaptcha-action={signupActionType} />
      {getFormBody()}
    </div>
  );
}

const getErrorMessageFromAsyncSignupResponse = (data: any): string => {
  let errorMessage = '';
  if (data.passwordErrorMsg !== undefined && data.passwordErrorMsg !== '') {
    errorMessage = data.passwordErrorMsg;
  } else if (data.location === 'blocked') {
    errorMessage = window.i18next.t('pmwjs_auth_country_blocked');
  } else if (data.email === 'exists') {
    // The provider linked to the address is deliberately not named — see `asyncSignup`.
    errorMessage = window.i18next.t('pmwjs_email_exists', {
      supportLink: window.PMW.util.site_url('user/feedbackform'),
      className: 'content-danger',
    });
  } else if (data.email === 'invalid' || data.params === 'missing') {
    errorMessage = window.i18next.t('pmwjs_signup_invalid_params_error');
  } else if (data.action === 'reject' || data.reason === SignupFailureReason.TOO_MANY_ATTEMPTS_FROM_DEVICE) {
    errorMessage = data.message;
  }
  return errorMessage;
};
